/**
 * OfflineSyncDatabase.js
 *
 * SQLite-backed persistent storage for the offline-first order queue.
 * Replaces the AsyncStorage-based OfflineQueue for primary data persistence.
 *
 * Schema – PendingOrders:
 *   id            TEXT PRIMARY KEY   – Local UUID (same id sent to backend for idempotency)
 *   order_data    TEXT               – JSON blob of the full order
 *   sync_status   TEXT               – 'Pending' | 'Syncing' | 'Failed'
 *   retry_count   INTEGER DEFAULT 0
 *   timestamp     TEXT               – ISO-8601 string
 *   next_retry_at TEXT               – ISO-8601 string for scheduled retry time
 *   first_failure_at TEXT            – ISO-8601 string for first failed sync attempt
 *   error_message TEXT               – Last error message (nullable)
 */

import {Platform} from 'react-native';
import {generateObjectId} from '../objectId';
import {generateHappyId} from '../happyId';

// Set to 0 to disable app-level payload byte cap.
const MAX_QUEUE_JSON_BYTES = 0;
const MAX_RETAINED_FAILED_ROWS = 5000;
const MAX_ERROR_MESSAGE_LENGTH = 600;

const normalizeErrorMessage = (message = '') => {
  const text = String(message || '');
  if (text.length <= MAX_ERROR_MESSAGE_LENGTH) {
    return text;
  }

  return `${text.slice(0, MAX_ERROR_MESSAGE_LENGTH)}...`;
};

const isSqliteFullError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('sqlite_full') || message.includes('disk is full') || message.includes('code 13');
};

const compactCartItemForQueue = (item = {}) => {
  const resolvedItemId = item?.itemId || item?.item?._id || (typeof item?.item === 'string' ? item.item : null);
  const sizeIndex = Number.isFinite(item?.sizeIndex) ? item.sizeIndex : 0;
  const resolvedSize = item?.size
    || item?.sizes?.[sizeIndex]?._id
    || item?.item?.sizes?.[sizeIndex]?._id
    || null;

  return {
    item: resolvedItemId || item?.item || null,
    itemId: resolvedItemId || undefined,
    sizeIndex,
    size: resolvedSize,
    quantity: item?.quantity || item?.amount || 1,
    amount: item?.amount || item?.quantity || 1,
    extras: Array.isArray(item?.extras) ? item.extras : [],
    serveType: item?.serveType || '',
    comments: Array.isArray(item?.comments) ? item.comments : [],
    discounts: item?.discounts || {},
    round: item?.round || 0,
    price: item?.price,
    remotePrice: item?.remotePrice || item?.price,
    name: item?.name || '',
  };
};

const compactOrderForQueue = (orderData = {}) => {
  const userData = orderData?.userData
    ? {
        orderType: orderData.userData?.orderType,
        addressInfo: orderData.userData?.addressInfo || {},
        userInfo: orderData.userData?.userInfo || {},
        selectedPortal: orderData.userData?.selectedPortal,
        preOrder: orderData.userData?.preOrder || null,
      }
    : null;

  const order = orderData?.order
    ? {
        _id: orderData.order?._id,
        generatedId: orderData.order?.generatedId,
        orderCode: orderData.order?.orderCode,
        tableId: orderData.order?.tableId,
        tableName: orderData.order?.tableName,
        status: orderData.order?.status,
        storno: Boolean(orderData.order?.storno),
      }
    : null;

  return {
    cartTotal: orderData?.cartTotal,
    cartItems: Array.isArray(orderData?.cartItems) ? orderData.cartItems.map(compactCartItemForQueue) : [],
    paymentType: orderData?.paymentType,
    paidAmount: orderData?.paidAmount,
    discountsForWholeCart: orderData?.discountsForWholeCart,
    vatInvoiceData: orderData?.vatInvoiceData || null,
    needInvoice: Boolean(orderData?.needInvoice),
    currentTable: orderData?.currentTable
      ? {
          _id: orderData.currentTable?._id,
          name: orderData.currentTable?.name,
          restaurantPlace: orderData.currentTable?.restaurantPlace,
        }
      : null,
    serviceFeeAmount: orderData?.serviceFeeAmount,
    isSavedForLater: Boolean(orderData?.isSavedForLater),
    alreadySentCartItems: Array.isArray(orderData?.alreadySentCartItems)
      ? orderData.alreadySentCartItems.map(compactCartItemForQueue)
      : [],
    orderId: orderData?.orderId,
    paymentTypes: Array.isArray(orderData?.paymentTypes) ? orderData.paymentTypes : [],
    tip: orderData?.tip,
    selectedVipCard: orderData?.selectedVipCard ? { _id: orderData.selectedVipCard?._id } : null,
    userData,
    selectedItemsToPay: Array.isArray(orderData?.selectedItemsToPay) ? orderData.selectedItemsToPay : [],
    order,
    proform: Boolean(orderData?.proform),
    deliveryFee: orderData?.deliveryFee || 0,
    _stableGeneratedId: orderData?._stableGeneratedId,
    _stableOrderCode: orderData?._stableOrderCode,
  };
};

const makeStoragePressurePayload = (compacted = {}) => ({
  ...compacted,
  cartItems: (compacted?.cartItems || []).map((item) => ({
    item: item?.item || item?.itemId || null,
    itemId: item?.itemId,
    size: item?.size || null,
    sizeIndex: Number.isFinite(item?.sizeIndex) ? item.sizeIndex : 0,
    quantity: item?.quantity || item?.amount || 1,
    amount: item?.amount || item?.quantity || 1,
    price: item?.price,
    remotePrice: item?.remotePrice,
  })),
  alreadySentCartItems: (compacted?.alreadySentCartItems || []).map((item) => ({
    item: item?.item || item?.itemId || null,
    itemId: item?.itemId,
    size: item?.size || null,
    sizeIndex: Number.isFinite(item?.sizeIndex) ? item.sizeIndex : 0,
    quantity: item?.quantity || item?.amount || 1,
    amount: item?.amount || item?.quantity || 1,
    price: item?.price,
    remotePrice: item?.remotePrice,
  })),
  selectedItemsToPay: [],
});

const pruneRowsUnderStoragePressure = async (db) => {
  if (db.__isShim) {
    const rows = await db.getAllAsync('SELECT * FROM PendingOrders');
    const failedRows = rows.filter((row) => row.sync_status === 'Failed');
    const overflow = failedRows.length - MAX_RETAINED_FAILED_ROWS;
    if (overflow > 0) {
      const oldestFailed = [...failedRows]
        .sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')))
        .slice(0, overflow);
      for (const row of oldestFailed) {
        await db._delete(row.id);
      }
    }
    return;
  }

  await db.runAsync(
    `DELETE FROM PendingOrders
     WHERE id IN (
       SELECT id FROM PendingOrders
       WHERE sync_status = 'Failed'
       ORDER BY timestamp ASC
       LIMIT (
         SELECT CASE
           WHEN COUNT(*) > ? THEN COUNT(*) - ?
           ELSE 0
         END
         FROM PendingOrders
         WHERE sync_status = 'Failed'
       )
     )`,
    [MAX_RETAINED_FAILED_ROWS, MAX_RETAINED_FAILED_ROWS]
  );

  try {
    await db.execAsync('VACUUM;');
  } catch (_) {
    // Some sqlite implementations may reject VACUUM during active operations.
  }
};

// ---------------------------------------------------------------------------
// Web fallback – when expo-sqlite is not available (Electron / browser builds)
// we use a thin in-memory + AsyncStorage shim so the rest of the code is
// identical across platforms.
// ---------------------------------------------------------------------------

let _db = null;
let _dbInitPromise = null;

const SQLITE_PREPARE_NPE_PATTERN = "nativedatabase.prepareasync";

const shouldRecoverFromSqlitePrepareError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes(SQLITE_PREPARE_NPE_PATTERN) || message.includes('nullpointerexception');
};

/**
 * Detects when the underlying native SQLite handle has been released out from
 * under us. This happens on Android when the Activity is destroyed on app close
 * but the JS runtime survives: the next reopen reuses a stale `_db` reference
 * whose native shared object is already gone. Errors surface as either
 * "Cannot use shared object that was already released" or a type-cast failure
 * against `expo.modules.sqlite.NativeDatabase`.
 */
const isReleasedNativeHandleError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('already released')
    || message.includes('shared object')
    || message.includes('nativestatement')
    || message.includes('expo.modules.sqlite.nativedatabase');
};

const isRecoverableNativeDbError = (error) =>
  shouldRecoverFromSqlitePrepareError(error) || isReleasedNativeHandleError(error);

const resetNativeDatabaseHandle = () => {
  _db = null;
  _dbInitPromise = null;
};

const isNativePlatform = Platform.OS === 'android' || Platform.OS === 'ios';

/**
 * Run a database operation with one automatic recovery attempt if the native
 * SQLite handle was released (see {@link isReleasedNativeHandleError}). On
 * recovery we drop the stale handle, reopen the database (which re-runs the
 * CREATE TABLE / pragma bootstrap), and replay the operation once. Non-native
 * platforms (shim) and unrecognised errors are re-thrown unchanged.
 *
 * @template T
 * @param {(db: any) => Promise<T>} operation
 * @returns {Promise<T>}
 */
const withDatabase = async (operation) => {
  const db = await getDatabase();
  try {
    return await operation(db);
  } catch (error) {
    if (isNativePlatform && !db?.__isShim && isRecoverableNativeDbError(error)) {
      console.warn('[OfflineSyncDB] Native SQLite handle was released – reopening and retrying once', error?.message);
      resetNativeDatabaseHandle();
      const recoveredDb = await getDatabase();
      return await operation(recoveredDb);
    }
    throw error;
  }
};

/* ---------- Native (expo-sqlite) implementation ---------- */
const getDatabase = async () => {
  if (_db) return _db;

  if (_dbInitPromise) {
    return _dbInitPromise;
  }

  if (isNativePlatform) {
    _dbInitPromise = (async () => {
      try {
        const SQLite = require('expo-sqlite');
        const nativeDb = await SQLite.openDatabaseAsync('offline_sync.db');

        try {
          // Keep SQLite ceiling high; actual limit is device/app free storage.
          await nativeDb.execAsync('PRAGMA journal_mode=WAL;');
          await nativeDb.execAsync('PRAGMA temp_store=MEMORY;');
          await nativeDb.execAsync('PRAGMA max_page_count=4294967294;');
        } catch (_) {
          // Some builds may not support all pragmas; continue with defaults.
        }

        // Create table if it doesn't exist
        await nativeDb.execAsync(`
          CREATE TABLE IF NOT EXISTS PendingOrders (
            id            TEXT PRIMARY KEY NOT NULL,
            order_data    TEXT NOT NULL,
            sync_status   TEXT NOT NULL DEFAULT 'Pending',
            retry_count   INTEGER NOT NULL DEFAULT 0,
            timestamp     TEXT NOT NULL,
            next_retry_at TEXT,
            first_failure_at TEXT,
            error_message TEXT
          );
        `);

        try {
          await nativeDb.execAsync(`ALTER TABLE PendingOrders ADD COLUMN next_retry_at TEXT;`);
        } catch (_) { /* column already exists */ }

        try {
          await nativeDb.execAsync(`ALTER TABLE PendingOrders ADD COLUMN first_failure_at TEXT;`);
        } catch (_) { /* column already exists */ }

        // Create index on sync_status for fast queue queries
        await nativeDb.execAsync(`
          CREATE INDEX IF NOT EXISTS idx_pending_orders_status ON PendingOrders(sync_status);
        `);

        // Composite index that matches the exact predicate used by
        // getNextPendingOrder (status + due-time scan ordered by timestamp).
        // Without this, SQLite falls back to a full scan + sort once the
        // queue grows beyond a few hundred rows.
        try {
          await nativeDb.execAsync(`
            CREATE INDEX IF NOT EXISTS idx_pending_orders_due
            ON PendingOrders(sync_status, next_retry_at, timestamp);
          `);
        } catch (_) { /* ignore – older sqlite may complain about NULL sort */ }

        _db = nativeDb;
        return _db;
      } catch (err) {
        console.warn('[OfflineSyncDB] expo-sqlite unavailable, falling back to AsyncStorage shim', err.message);
      } finally {
        _dbInitPromise = null;
      }

      // Fallback – AsyncStorage-based shim (web / electron)
      _db = createAsyncStorageShim();
      return _db;
    })();

    return _dbInitPromise;
  }

  // Fallback – AsyncStorage-based shim (web / electron)
  _db = createAsyncStorageShim();
  return _db;
};

/* ---------- AsyncStorage shim for web ---------- */
function createAsyncStorageShim() {
  const AsyncStorage = require('@react-native-async-storage/async-storage').default;
  const STORAGE_KEY = 'offline_sync_pending_orders';

  const loadAll = async () => {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  };

  const saveAll = async (rows) => {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  };

  return {
    __isShim: true,
    runAsync: async (sql, params = []) => {
      // Minimal SQL-like operations mapped to array manipulations
      // Not a real SQL engine – only covers the queries we emit below.
      return undefined;
    },
    getFirstAsync: async (sql, params = []) => {
      const rows = await loadAll();
      if (sql.includes('COUNT')) {
        const status = params[0];
        const count = status
          ? rows.filter(r => r.sync_status === status).length
          : rows.length;
        return { count };
      }
      if ((sql && sql.includes('WHERE id')) || (!sql && params.length === 1)) {
        return rows.find(r => r.id === params[0]) || null;
      }
      if (sql.includes('MIN(next_retry_at)')) {
        const eligible = rows
          .filter(r => r.sync_status === 'Pending' && r.next_retry_at)
          .sort((a, b) => a.next_retry_at.localeCompare(b.next_retry_at));
        return eligible[0] ? { next_retry_at: eligible[0].next_retry_at } : null;
      }
      // ORDER BY timestamp ASC LIMIT 1
      const nowIso = new Date().toISOString();
      const pending = rows
        .filter(r => r.sync_status === 'Pending' && (!r.next_retry_at || r.next_retry_at <= nowIso))
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      return pending[0] || null;
    },
    getAllAsync: async (sql, params = []) => {
      const rows = await loadAll();
      if (params.length === 0) {
        return rows.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      }
      // WHERE sync_status IN (?, ?)
      return rows
        .filter(r => params.includes(r.sync_status))
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    },
    execAsync: async (sql) => {
      // DDL – no-op for shim
    },
    // Custom helpers for shim operations
    _insert: async (row) => {
      const rows = await loadAll();
      rows.push(row);
      await saveAll(rows);
    },
    _update: async (id, changes) => {
      const rows = await loadAll();
      const idx = rows.findIndex(r => r.id === id);
      if (idx !== -1) {
        rows[idx] = { ...rows[idx], ...changes };
        await saveAll(rows);
      }
    },
    _delete: async (id) => {
      const rows = await loadAll();
      await saveAll(rows.filter(r => r.id !== id));
    },
    _deleteCompleted: async () => {
      const rows = await loadAll();
      await saveAll(rows.filter(r => r.sync_status !== 'Synced'));
    },
  };
}

/* ========================================================================= */
/*  Public API                                                                */
/* ========================================================================= */

/**
 * Insert a new pending order into the queue.
 * @param {Object} orderData – Full order payload
 * @param {string} [id]      – Optional pre-generated UUID
 * @returns {Promise<string>} The order id
 */
export const insertPendingOrder = async (orderData, id) => {
  const orderId = String(id || generateObjectId());
  const timestamp = new Date().toISOString();

  // ── Stamp stable IDs at insertion time ──────────────────────────────────
  // These IDs must NEVER be regenerated on retry. The SyncManager reads them
  // from order_data and sends them to the server so every attempt uses the
  // same generatedId / orderCode.  Without this, each retry would create a
  // brand-new order on the backend.
  const enrichedOrderData = {
    ...orderData,
    _stableGeneratedId: orderData._stableGeneratedId || orderId,
    _stableOrderCode:   orderData._stableOrderCode   || generateHappyId(),
  };

  const compactedOrderData = compactOrderForQueue(enrichedOrderData);
  let orderJson = JSON.stringify(compactedOrderData);
  if (MAX_QUEUE_JSON_BYTES > 0 && orderJson.length > MAX_QUEUE_JSON_BYTES) {
    orderJson = JSON.stringify(makeStoragePressurePayload(compactedOrderData));
  }

  const insertRow = (db, rowJson) => {
    if (db.__isShim) {
      return db._insert({
        id: orderId,
        order_data: rowJson,
        sync_status: 'Pending',
        retry_count: 0,
        timestamp,
        next_retry_at: timestamp,
        first_failure_at: null,
        error_message: null,
      });
    }

    return db.runAsync(
      `INSERT OR REPLACE INTO PendingOrders (id, order_data, sync_status, retry_count, timestamp, next_retry_at, first_failure_at, error_message)
       VALUES (?, ?, 'Pending', 0, ?, ?, NULL, NULL)`,
      [orderId, String(rowJson || '{}'), timestamp, timestamp]
    );
  };

  try {
    // withDatabase handles the "native handle released on reopen" recovery;
    // here we only need to layer the SQLITE_FULL pruning fallback on top.
    await withDatabase((db) => insertRow(db, orderJson));
  } catch (error) {
    if (isSqliteFullError(error)) {
      console.warn('[OfflineSyncDB] SQLITE_FULL while inserting order, pruning queue and retrying once');
      const emergencyJson = JSON.stringify(makeStoragePressurePayload(compactedOrderData));
      await withDatabase(async (db) => {
        await pruneRowsUnderStoragePressure(db);
        await insertRow(db, emergencyJson);
      });
    } else {
      throw error;
    }
  }

  console.log(`[OfflineSyncDB] Order ${orderId} inserted as Pending`);
  return orderId;
};

/**
 * Retrieve the next Pending order (oldest first).
 * @returns {Promise<Object|null>} Parsed row or null
 */
export const getNextPendingOrder = async () => {
  const nowIso = new Date().toISOString();
  const row = await withDatabase((db) => db.getFirstAsync(
    `SELECT * FROM PendingOrders WHERE sync_status = 'Pending' AND (next_retry_at IS NULL OR next_retry_at <= ?) ORDER BY timestamp ASC LIMIT 1`,
    [nowIso]
  ));
  if (!row) return null;
  return { ...row, order_data: JSON.parse(row.order_data) };
};

/**
 * Returns milliseconds until the next scheduled retry, or null when none exists.
 */
export const getNextRetryDelayMs = async () => {
  const row = await withDatabase((db) => db.getFirstAsync(
    `SELECT MIN(next_retry_at) as next_retry_at FROM PendingOrders WHERE sync_status = 'Pending' AND next_retry_at IS NOT NULL`
  ));

  if (!row?.next_retry_at) return null;

  const delayMs = new Date(row.next_retry_at).getTime() - Date.now();
  return Math.max(0, delayMs);
};

/**
 * Fetch all orders with a given status (or all).
 * @param {string[]} [statuses] – e.g. ['Pending','Failed']
 * @returns {Promise<Object[]>}
 */
export const getOrdersByStatus = async (statuses) => {
  const rows = await withDatabase((db) => {
    if (!statuses || statuses.length === 0) {
      return db.getAllAsync(`SELECT * FROM PendingOrders ORDER BY timestamp ASC`);
    }
    const placeholders = statuses.map(() => '?').join(',');
    return db.getAllAsync(
      `SELECT * FROM PendingOrders WHERE sync_status IN (${placeholders}) ORDER BY timestamp ASC`,
      statuses
    );
  });
  return rows.map(r => ({ ...r, order_data: JSON.parse(r.order_data) }));
};

/**
 * Count orders by status.
 * @param {string} [status]
 * @returns {Promise<number>}
 */
export const countOrdersByStatus = async (status) => {
  const row = await withDatabase((db) => (status
    ? db.getFirstAsync(`SELECT COUNT(*) as count FROM PendingOrders WHERE sync_status = ?`, [status])
    : db.getFirstAsync(`SELECT COUNT(*) as count FROM PendingOrders`)));
  return row?.count ?? 0;
};

/**
 * Mark an order as Syncing (about to POST).
 */
export const markOrderSyncing = async (id) => {
  await withDatabase((db) => (db.__isShim
    ? db._update(id, { sync_status: 'Syncing' })
    : db.runAsync(`UPDATE PendingOrders SET sync_status = 'Syncing' WHERE id = ?`, [id])));
};

/**
 * Mark an order as successfully synced and remove it.
 */
export const markOrderSynced = async (id) => {
  await withDatabase((db) => (db.__isShim
    ? db._delete(id)
    : db.runAsync(`DELETE FROM PendingOrders WHERE id = ?`, [id])));
  console.log(`[OfflineSyncDB] Order ${id} synced & removed`);
};

/**
 * Mark an order as Failed and record the error.
 */
export const markOrderFailed = async (id, errorMessage = '') => {
  const nowIso = new Date().toISOString();
  const safeErrorMessage = normalizeErrorMessage(errorMessage);
  await withDatabase(async (db) => {
    if (db.__isShim) {
      await db._update(id, {
        sync_status: 'Failed',
        retry_count: ((await db.getFirstAsync('', [id]))?.retry_count || 0) + 1,
        first_failure_at: (await db.getFirstAsync('', [id]))?.first_failure_at || nowIso,
        error_message: safeErrorMessage,
      });
      // Fix retry_count for shim
      const rows = await db.getAllAsync('SELECT * FROM PendingOrders');
      const row = rows.find(r => r.id === id);
      if (row && row.retry_count === undefined) {
        await db._update(id, { retry_count: 1 });
      }
      return;
    }
    await db.runAsync(
      `UPDATE PendingOrders SET sync_status = 'Failed', retry_count = retry_count + 1, error_message = ?, first_failure_at = COALESCE(first_failure_at, ?) WHERE id = ?`,
      [safeErrorMessage, nowIso, id]
    );
  });
};

/**
 * Schedule an automatic retry after delayMs, without immediately retrying in a tight loop.
 */
export const scheduleOrderRetry = async (id, errorMessage = '', delayMs = 10000) => {
  const nowIso = new Date().toISOString();
  const nextRetryAt = new Date(Date.now() + delayMs).toISOString();
  const safeErrorMessage = normalizeErrorMessage(errorMessage);

  await withDatabase(async (db) => {
    if (db.__isShim) {
      const existing = await db.getFirstAsync('', [id]);
      await db._update(id, {
        sync_status: 'Pending',
        retry_count: (existing?.retry_count || 0) + 1,
        error_message: safeErrorMessage,
        first_failure_at: existing?.first_failure_at || nowIso,
        next_retry_at: nextRetryAt,
      });
      return;
    }
    await db.runAsync(
      `UPDATE PendingOrders
       SET sync_status = 'Pending',
           retry_count = retry_count + 1,
           error_message = ?,
           first_failure_at = COALESCE(first_failure_at, ?),
           next_retry_at = ?
       WHERE id = ?`,
      [safeErrorMessage, nowIso, nextRetryAt, id]
    );
  });
};

/**
 * Reset a Failed order back to Pending for retry.
 */
export const resetOrderToPending = async (id) => {
  const nowIso = new Date().toISOString();
  await withDatabase((db) => (db.__isShim
    ? db._update(id, { sync_status: 'Pending', error_message: null, next_retry_at: nowIso })
    : db.runAsync(
        `UPDATE PendingOrders SET sync_status = 'Pending', error_message = NULL, next_retry_at = ? WHERE id = ?`,
        [nowIso, id]
      )));
};

/**
 * Reset ALL Failed orders back to Pending (Retry All).
 */
export const resetAllFailedOrders = async () => {
  const nowIso = new Date().toISOString();
  await withDatabase(async (db) => {
    if (db.__isShim) {
      const rows = await db.getAllAsync('SELECT * FROM PendingOrders');
      for (const row of rows) {
        if (row.sync_status === 'Failed') {
          await db._update(row.id, { sync_status: 'Pending', error_message: null, next_retry_at: nowIso });
        }
      }
      return;
    }
    await db.runAsync(
      `UPDATE PendingOrders SET sync_status = 'Pending', error_message = NULL, next_retry_at = ? WHERE sync_status = 'Failed'`,
      [nowIso]
    );
  });
};

/**
 * Reset orders stuck in 'Syncing' status back to 'Pending'.
 * This handles the case where the app was killed mid-sync and
 * orders were left in an intermediate state.
 */
export const resetStuckSyncingOrders = async () => {
  const nowIso = new Date().toISOString();
  await withDatabase(async (db) => {
    if (db.__isShim) {
      const rows = await db.getAllAsync('SELECT * FROM PendingOrders');
      for (const row of rows) {
        if (row.sync_status === 'Syncing') {
          await db._update(row.id, { sync_status: 'Pending', next_retry_at: nowIso });
        }
      }
      return;
    }
    await db.runAsync(
      `UPDATE PendingOrders SET sync_status = 'Pending', next_retry_at = ? WHERE sync_status = 'Syncing'`,
      [nowIso]
    );
  });
};

/**
 * Get a specific order by ID.
 */
export const getOrderById = async (id) => {
  const row = await withDatabase((db) => db.getFirstAsync(
    `SELECT * FROM PendingOrders WHERE id = ?`,
    [id]
  ));
  if (!row) return null;
  return { ...row, order_data: JSON.parse(row.order_data) };
};

/**
 * Delete a specific order from the queue (manual dismissal).
 */
export const deleteOrder = async (id) => {
  const db = await getDatabase();
  if (db.__isShim) {
    await db._delete(id);
  } else {
    await db.runAsync(`DELETE FROM PendingOrders WHERE id = ?`, [id]);
  }
};

/**
 * Purge all completed (synced) orders – cleanup utility.
 */
export const purgeCompletedOrders = async () => {
  const db = await getDatabase();
  if (db.__isShim) {
    await db._deleteCompleted();
  } else {
    await db.runAsync(`DELETE FROM PendingOrders WHERE sync_status = 'Synced'`);
  }
};

export default {
  insertPendingOrder,
  getNextPendingOrder,
  getOrdersByStatus,
  countOrdersByStatus,
  markOrderSyncing,
  markOrderSynced,
  markOrderFailed,
  scheduleOrderRetry,
  resetOrderToPending,
  resetAllFailedOrders,
  resetStuckSyncingOrders,
  getNextRetryDelayMs,
  getOrderById,
  deleteOrder,
  purgeCompletedOrders,
};

